import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import EmailEditor from "react-email-editor";
import "../styles/EditorWorkspace.css";

import MERGE_TAGS from "../constants/mergeTags";
import { flattenMergeTagsForUnlayer } from "../utils/flattenMergeTags";
import MergeTagPicker from "../components/merge-tags/MergeTagPicker";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agent-book-crm-backend.onrender.com";

const UNLAYER_OPTIONS = {
  projectId: 277125,
  appearance: { theme: "modern" },
  features: {
    stockImages: true,
    undoRedo: true,
    preview: true,
  },
};

export default function TemplateEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isEditing = Boolean(id);

  const clientId = location.state?.clientId || null;
  const leadId = location.state?.leadId || null;

  const editorRef = useRef(null);
  const subjectRef = useRef(null);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [showSubjectTags, setShowSubjectTags] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editorReady, setEditorReady] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(isEditing);
  const [designApplied, setDesignApplied] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState(null);

  const [policies, setPolicies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedContext, setSelectedContext] = useState(null);

  const FLAT_MERGE_TAGS = flattenMergeTagsForUnlayer(MERGE_TAGS);

  useEffect(() => {
    async function loadContext() {
      if (!clientId && !leadId) return;

      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams();
        if (clientId) params.append("clientId", clientId);
        if (leadId) params.append("leadId", leadId);

        const res = await fetch(
          `${API_BASE_URL}/api/email-context?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = await res.json();
        setPolicies(data.policies || []);
        setOpportunities(data.opportunities || []);
      } catch (err) {
        console.error("Failed to load email context:", err);
      }
    }

    loadContext();
  }, [clientId, leadId]);

  useEffect(() => {
    if (!isEditing) {
      setTemplateLoading(false);
      return;
    }

    let isMounted = true;

    async function loadTemplate() {
      try {
        setTemplateLoading(true);

        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE_URL}/api/templates/email/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error("Failed to load template");
        }

        const tpl = await res.json();

        if (!isMounted) return;

        setLoadedTemplate(tpl);
        setName(tpl.name || "");
        setSubject(tpl.subject || "");
      } catch (err) {
        console.error("Load template failed:", err);
        alert("Failed to load template.");
      } finally {
        if (isMounted) {
          setTemplateLoading(false);
        }
      }
    }

    loadTemplate();

    return () => {
      isMounted = false;
    };
  }, [id, isEditing]);

  useEffect(() => {
    if (!editorReady) return;
    if (!editorRef.current?.editor) return;
    if (!isEditing) return;
    if (!loadedTemplate) return;
    if (designApplied) return;

    try {
      const rawDesign = loadedTemplate.designJson ?? loadedTemplate.design_json ?? null;

      if (rawDesign) {
        const parsedDesign =
          typeof rawDesign === "string" ? JSON.parse(rawDesign) : rawDesign;

        editorRef.current.editor.loadDesign(parsedDesign);
      }

      setDesignApplied(true);
    } catch (err) {
      console.error("Failed to load design into editor:", err);
    }
  }, [editorReady, isEditing, loadedTemplate, designApplied]);

  const insertAtCursor = (ref, value) => {
    const el = ref.current;
    if (!el) return;

    const start = el.selectionStart;
    const end = el.selectionEnd;

    setSubject(el.value.slice(0, start) + value + el.value.slice(end));

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + value.length, start + value.length);
    });

    setShowSubjectTags(false);
  };

  const onReady = (editor) => {
    editor.setMergeTags(FLAT_MERGE_TAGS);
    setEditorReady(true);
  };

  const handleSave = () => {
    if (!editorRef.current?.editor) {
      alert("Editor not ready yet.");
      return;
    }

    if (!name.trim() || !subject.trim()) {
      alert("Template name and subject are required.");
      return;
    }

    setSaving(true);

    editorRef.current.editor.exportHtml(async ({ design, html }) => {
      try {
        const token = localStorage.getItem("token");

        const payload = {
          name: name.trim(),
          subject: subject.trim(),
          body: html,
          designJson: design,
        };

        const res = await fetch(
          isEditing
            ? `${API_BASE_URL}/api/templates/email/${id}`
            : `${API_BASE_URL}/api/templates/email`,
          {
            method: isEditing ? "PUT" : "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const errText = await res.text();
          console.error("Template save failed:", errText);
          throw new Error("Save failed");
        }

        navigate("/templates");
      } catch (err) {
        alert(err.message);
      } finally {
        setSaving(false);
      }
    });
  };

  const showLoadingOverlay = !editorReady || templateLoading;

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page template-editor-page">
        <div className="sales-hero template-editor-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">
              {isEditing ? "Edit Email Template" : "New Email Template"}
            </h1>
            <p className="sales-subtitle">
              Build polished email templates with merge tags, preview context, and drag-and-drop editing.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() => navigate("/templates")}
            >
              Back to Templates
            </button>

            <button
              className="sales-primary-btn"
              onClick={handleSave}
              disabled={saving || showLoadingOverlay}
            >
              {saving
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                  ? "Save Changes"
                  : "Create Template"}
            </button>
          </div>
        </div>

        <div className="template-editor-grid">
          <div className="sales-section-card template-editor-sidebar">
            <div className="sales-section-head">
              <h2>Template Details</h2>
              <p>Name, subject line, and optional preview context.</p>
            </div>

            <div className="template-editor-fields">
              {(policies.length > 0 || opportunities.length > 0) && (
                <div className="template-field">
                  <label>Preview Context (optional)</label>
                  <select
                    value={
                      selectedContext
                        ? `${selectedContext.type}:${selectedContext.id}`
                        : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setSelectedContext(null);
                        return;
                      }

                      const [type, contextId] = value.split(":");
                      setSelectedContext({ type, id: contextId });
                    }}
                  >
                    <option value="">Select Policy / Opportunity</option>
                    {policies.map((p) => (
                      <option key={`policy-${p.id}`} value={`policy:${p.id}`}>
                        {p.label}
                      </option>
                    ))}
                    {opportunities.map((o) => (
                      <option
                        key={`opportunity-${o.id}`}
                        value={`opportunity:${o.id}`}
                      >
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="template-field">
                <label>Template Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter template name"
                />
              </div>

              <div className="template-field template-subject-field">
                <label>Subject Line</label>
                <input
                  ref={subjectRef}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject line"
                />

                <button
                  type="button"
                  className="template-inline-link"
                  onClick={() => setShowSubjectTags((prev) => !prev)}
                >
                  Insert merge tag
                </button>

                {showSubjectTags && (
                  <div className="template-merge-picker-wrap">
                    <MergeTagPicker
                      onInsert={(tag) => insertAtCursor(subjectRef, tag)}
                      onClose={() => setShowSubjectTags(false)}
                    />
                  </div>
                )}
              </div>

              <div className="template-helper-card">
                <h3>Quick Tips</h3>
                <ul>
                  <li>Use merge tags to personalize names, policies, and opportunities.</li>
                  <li>Keep subject lines short and specific.</li>
                  <li>Preview context helps test how dynamic content will appear.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="sales-section-card template-editor-main">
            <div className="sales-section-head">
              <h2>Email Designer</h2>
              <p>Drag, drop, and customize your email layout.</p>
            </div>

            <div
              className="template-editor-canvas"
              style={{ position: "relative" }}
            >
              {showLoadingOverlay && (
                <div className="template-editor-loading">
                  {templateLoading ? "Loading template..." : "Loading editor..."}
                </div>
              )}

              <EmailEditor
                ref={editorRef}
                onReady={onReady}
                options={UNLAYER_OPTIONS}
                style={{ minHeight: 640 }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}